/** Couleur d'avatar persistée : cohérente sur toutes les pages (voir gestion-equipes, site, user-list-modal). */

const DEFAULT_COLOR = '#513E9B';

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
  const num = parseInt(full, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function lighten(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}

function toRgba(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function avatarStyle(hex: string | null | undefined, opts?: { active?: boolean }): Record<string, string> {
  const color = hex || DEFAULT_COLOR;
  const ring = opts?.active ? `0 0 0 2px #fff, 0 0 0 3.5px ${color}, ` : '';
  return {
    background: `linear-gradient(135deg, ${lighten(color, 0.4)}, ${color})`,
    'box-shadow': `inset 0 1px 1px rgba(255, 255, 255, 0.35), ${ring}0 2px 6px -1px ${toRgba(color, 0.35)}`,
  };
}

const RAINBOW_COLORS = ['#513E9B', '#FF2B98', '#22C55E', '#EAB308', '#3B82F6', '#0D9488'];

export function rainbowStyle(opts?: { active?: boolean }): Record<string, string> {
  const ring = opts?.active ? '0 0 0 2px #fff, 0 0 0 3.5px #513E9B, ' : '';
  return {
    background: `conic-gradient(from 180deg, ${RAINBOW_COLORS.join(', ')}, ${RAINBOW_COLORS[0]})`,
    'box-shadow': `inset 0 1px 1px rgba(255, 255, 255, 0.35), ${ring}0 2px 6px -1px rgba(0, 0, 0, 0.18)`,
  };
}

export interface AvatarColorable {
  avatar_color?: string | null;
  job_title_ids?: string[];
}

/**
 * Résout le style d'avatar d'un utilisateur :
 * - aucun type de poste → couleur individuelle persistée (avatar_color)
 * - un seul type de poste → couleur du type de poste
 * - plusieurs types de poste → dégradé arc-en-ciel
 */
export function resolveAvatarStyle(
  u: AvatarColorable,
  jobTitleColor: (id: string) => string | undefined,
  opts?: { active?: boolean }
): Record<string, string> {
  const ids = u.job_title_ids ?? [];
  if (ids.length > 1) return rainbowStyle(opts);
  if (ids.length === 1) {
    const color = jobTitleColor(ids[0]);
    if (color) return avatarStyle(color, opts);
  }
  return avatarStyle(u.avatar_color, opts);
}
